import { useEffect, useState, type FormEvent } from "react";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { displayError, formatDate } from "../lib/format";
import type { AccountLoginJob } from "../lib/types";

export function LoginCaptchaForm({ job }: { job: AccountLoginJob }) {
  const challenge = job.captcha;
  const [image, setImage] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setImage(""); setCode(""); setSubmitted(false); setError(null);
    if (!challenge) return;
    const controller = new AbortController();
    api.accountLoginCaptcha(job.id, challenge.id, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setImage(result.image); })
      .catch((caught) => { if (!controller.signal.aborted) setError(displayError(caught)); });
    return () => controller.abort();
  }, [job.id, challenge?.id]);

  if (!challenge) return null;
  const waiting = !submitted && challenge.status === "waiting";
  const expired = Date.parse(challenge.expires_at) <= Date.now();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!challenge || !code.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      await api.submitAccountLoginCaptcha(job.id, challenge.id, code.trim());
      setCode(""); setSubmitted(true);
    } catch (caught) {
      setError(displayError(caught));
    } finally { setBusy(false); }
  }

  return (
    <section className="login-captcha-card" aria-label={`验证码 ${job.email || job.id}`}>
      <div className="login-captcha-heading"><ShieldCheck size={18} /><strong>{waiting ? "请完成人工验证码" : "验证码已提交，正在继续登录"}</strong></div>
      <p>输入下方截图中的验证码，提交后将自动继续当前账号的登录。</p>
      {image ? <><div className={`login-captcha-preview${expanded ? " expanded" : ""}`}><img className="login-captcha-image" src={image} alt="Google 当前登录页面的验证码截图" /></div><button className="button button-secondary button-small" type="button" onClick={() => setExpanded(!expanded)}>{expanded ? "收起截图" : "展开完整截图"}</button></> : !error && <span className="muted"><LoaderCircle className="spin" size={16} /> 正在加载验证码截图</span>}
      <form className="login-captcha-answer" onSubmit={(event) => void submit(event)}>
        <label className="field"><span>验证码</span><input value={code} onChange={(event) => setCode(event.target.value)} placeholder="输入截图中的字符" aria-label="验证码" autoComplete="off" spellCheck={false} maxLength={128} disabled={!waiting || expired || busy} /></label>
        <button className="button button-primary" type="submit" disabled={!waiting || expired || busy || !image || !code.trim()}>{busy ? <LoaderCircle className="spin" size={16} /> : <ShieldCheck size={16} />}{waiting ? "提交并继续登录" : "等待登录结果"}</button>
      </form>
      <small className="muted">{expired ? "验证码已过期，请等待任务更新。" : `本次验证有效至 ${formatDate(challenge.expires_at)}`}</small>
      {error && <div className="form-error" role="alert">{error}</div>}
    </section>
  );
}
